import React from 'react';

import Seo from '../ui/Seo';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import Pledge from '../sections/Pledge';
import StoryDeck from '../sections/StoryDeck';
import SectionHead from '../sections/SectionHead';
import Bento from '../sections/Bento';
import Faqs from '../sections/Faqs';
import Cta from '../sections/Cta';

import PreventativeMaintenance from "../media/images/electrical-maintenance/preventative-maintenance.jpg";
import SystemDiagnostics from "../media/images/electrical-maintenance/system-diagnostics.jpg";
import EmergencyRepairs from "../media/images/electrical-maintenance/emergency-repairs.jpg";
import LightingMaintenance from "../media/images/electrical-maintenance/lighting-maintenance.jpg";
import SafetyInspections from "../media/images/electrical-maintenance/safety-inspections.jpg";
import GeneratorMaintenance from "../media/images/electrical-maintenance/generator-maintenance.jpg";
import ElectricalPanelUpgrades from "../media/images/electrical-maintenance/electrical-panel-upgrades.jpg";

const content =
[
    {
        title: "Lighting Maintenance",
        text: "Maintain bright and efficient lighting in your home or business. From replacing fixtures to upgrading to energy-efficient LEDs, our team ensures your property is well-lit and welcoming.",
        imgSrc: LightingMaintenance,
        alt: "Lighting Maintenance - Smart Electric Solutions",
        focus: "60%",
    },
    {
        title: "System Diagnostics",
        text: "Accurate diagnostics are key to maintaining optimal performance. Our electricians use state-of-the-art equipment to assess your electrical systems, identify inefficiencies, and recommend improvements.",
        imgSrc: SystemDiagnostics,
        alt: "System Diagnostics - Smart Electric Solutions",
        focus: "60%",
    },
    {
        title: "Emergency Repairs",
        text: "Electrical emergencies can happen at any time. We offer rapid response services to address urgent issues, minimize downtime, and ensure the safety of your property and occupants.",
        imgSrc: EmergencyRepairs,
        alt: "Emergency Repairs - Smart Electric Solutions",
        focus: "50%",
    },
    {
        title: "Preventative Maintenance",
        text: "Proactively identify and address potential electrical issues before they escalate. Our preventative maintenance services include inspecting wiring, circuit breakers, and panels to ensure your system is operating safely and efficiently.",
        imgSrc: PreventativeMaintenance,
        alt: "Preventative Maintenance - Smart Electric Solutions",
        focus: "50%",
    },
    {
        title: "Safety Inspections",
        text: "Electrical safety is our top priority. Our comprehensive safety inspections include testing outlets, switches, and protective devices to ensure compliance with current safety codes and regulations.",
        imgSrc: SafetyInspections,
        alt: "Safety Inspections - Smart Electric Solutions",
        focus: "50%",
    },
    {
        title: "Generator Maintenance",
        text: "Keep your generator ready when you need it most. We perform routine maintenance, testing, and fuel system checks to ensure reliable backup power for your property.",
        imgSrc: GeneratorMaintenance,
        alt: "Generator Maintenance - Smart Electric Solutions",
        focus: "55%",
    },
    {
        title: "Electrical Panel Upgrades",
        text: "Outdated electrical panels can lead to inefficiencies and safety hazards. Our panel upgrade services include maintenance to extend the life of your system and improve overall performance.",
        imgSrc: ElectricalPanelUpgrades,
        alt: "Electrical Panel Upgrades - Smart Electric Solutions",
        focus: "45%",
    }
];

const whyChoose =
[
    {
        title: "Experienced Electricians",
        text: "Our licensed and insured electricians have years of experience delivering quality maintenance services for residential and commercial properties.",
    },
    {
        title: "Comprehensive Solutions",
        text: "We offer a wide range of maintenance services, tailored to your property’s unique needs, ensuring everything from wiring to lighting systems operates flawlessly.",
    },
    {
        title: "Safety First",
        text: "We prioritize your safety, using rigorous testing and inspections to prevent potential hazards and keep your property compliant with all regulations.",
    },
    {
        title: "Reliable Service",
        text: "Count on us for prompt and dependable electrical maintenance, minimizing disruptions and maximizing the lifespan of your systems.",
    }
];

export default function ElectricalMaintenance() {
    return (
        <>
            <Seo
                title="Electrical Maintenance Services"
                description="Expert electrical maintenance: preventative maintenance, system diagnostics, emergency repairs, safety inspections, generator maintenance and panel upgrades."
                keywords="electrical maintenance, preventative maintenance, system diagnostics, emergency repairs, safety inspections, generator maintenance, electrical panel upgrades"
                path="/electrical-maintenance"
                schema={{
                    '@type': 'WebPage',
                    name: 'Electrical Maintenance Services',
                    description: 'Expert electrical maintenance: preventative maintenance, system diagnostics, emergency repairs, safety inspections, generator maintenance and panel upgrades.',
                    url: 'https://smartelectric.solutions/electrical-maintenance'
                }}
            />

            <PageHero
                eyebrow="Services"
                title="Maintenance is cheaper than an outage."
                lead="Scheduled inspection, thermal diagnostics and emergency response, so faults are found on a planned visit rather than on the worst possible day."
                backdrop={content[0].imgSrc}
                focus={`${content[0].focus} center`}
                crumbs={[{ label: 'Services' }, { label: 'Electrical Maintenance' }]}
            />

            <TradeTicker />

            <section className="section section--tight">
                <div className="shell">
                    <Pledge />
                </div>
            </section>

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead
                        eyebrow="What we do"
                        title="Keeping systems running."
                        lead="Planned maintenance and unplanned response, from a single fixture to a whole distribution board."
                    />
                </div>

                <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                    <StoryDeck items={content} />
                </div>
            </section>

            <Bento
                eyebrow="Why us"
                title="Why choose Smart Electric Solutions?"
                items={whyChoose.map(item => ({ title: item.title, body: item.text }))}
                cols={2}
            />

            <Faqs />
            <Cta />
        </>
    );
}
